import { BundleBuilderStoreMixinComponent } from '@theme/bundle-builder-mixin-store';

/**
 * @typedef {import('./bundle-builder-store.js').Product} Product
 * @typedef {import('./bundle-builder-store.js').Variant} Variant
 * @typedef {Record<string, import('./bundle-builder-mixin.js').BundleBuilderPropSpec>} BundleBuilderProps
 * @typedef {import('./bundle-builder-store.js').Collection} Collection
*/


const QUANTITY_INPUT_MOBILE_QUERY = '(max-width: 979px)';

export class BundleBuilderQuantitySelectorComponent extends BundleBuilderStoreMixinComponent {
  requiredRefs = ['quantitySelector', 'quantityInput', 'quantityPlusButton', 'quantityMinusButton', 'quantitySelectorMaxWarningTooltip', 'soldOutForNewMessage'];

  /** @type {MediaQueryList} */
  #quantityInputMobileQuery = window.matchMedia(QUANTITY_INPUT_MOBILE_QUERY);

  /** @type {BundleBuilderProps} */
  props = {
    productId: {
      type: 'number',
      required: true,
      attributeName: 'data-product-id',
    },
  };

  /** @type {number} */
  productId;
  /** @type {Product|undefined} */
  product;
  connectedCallback() {
    super.connectedCallback();
    this.product = this.getters.getProduct(this.productId);
    this.#syncQuantityInputId();
    this.#syncInputMobileBehavior();
    this.#quantityInputMobileQuery.addEventListener('change', this.#syncInputMobileBehavior);
  }

  disconnectedCallback() {
    this.#quantityInputMobileQuery.removeEventListener('change', this.#syncInputMobileBehavior);
    super.disconnectedCallback();
  }

  #syncQuantityInputId() {
    const input = /** @type {HTMLInputElement} */ (this.refs.quantityInput);
    const tooltip = /** @type {HTMLElement} */ (this.refs.quantitySelectorMaxWarningTooltip);
    const soldOutMessage = /** @type {HTMLElement} */ (this.refs.soldOutForNewMessage);
    const inputId = `bundle-builder-qty-${this.productId}`;
    const tooltipId = `bundle-builder-qty-max-warning-${this.productId}`;
    const soldOutMessageId = `bundle-builder-qty-sold-out-for-new-${this.productId}`;
    input.id = inputId;
    tooltip.id = tooltipId;
    soldOutMessage.id = soldOutMessageId;

    const label = this.querySelector('label.visually-hidden');
    if (label instanceof HTMLLabelElement) {
      label.htmlFor = inputId;
    }
  }

  get quantity() {
    return this.itemOnBundle?.quantity ?? 0;
  }

  get maxQuantity() {
    let max = this.quantity + this.getters.getItemsToMaximum();
    const maxQuantityPerVariant = this.state.bundleSettings?.max_quantity_per_variant;
    if (maxQuantityPerVariant && max > maxQuantityPerVariant) {
      max = maxQuantityPerVariant;
    }
    return max;
  }

  get minQuantity() {
    return /** @type {HTMLInputElement} */ (this.refs.quantityInput).min ? parseInt(/** @type {HTMLInputElement} */ (this.refs.quantityInput).min) : 0;
  }

  get isInBundle() {
    return !!this.itemOnBundle;
  }

  get itemOnBundle() {
    return this.state.items.find(item => item.productId === this.productId);
  }

  get price() {
    return this.itemOnBundle?.price ?? this.getters.getTierVariant(this.productId)?.price ?? 0;
  }

  get isSoldOut() {
    return this.getters.isProductFullySoldOut(this.productId);
  }

  get isQuantityIncreaseLocked() {
    return this.getters.isProductQuantityIncreaseLocked(this.productId);
  }

  get tierVariant() {
    return this.getters.getTierVariant(this.productId);
  }

  handleDecreaseQuantity() {
    this.#blurQuantityInput();
    this.mutations.setItemQuantity(this.productId, Math.max(this.minQuantity, this.quantity - 1));
  }

  handleIncreaseQuantity() {
    this.#blurQuantityInput();
    if (this.isSoldOut || this.isQuantityIncreaseLocked) return;
    const maxQuantityPerVariant = this.state.bundleSettings?.max_quantity_per_variant;
    if (maxQuantityPerVariant && (this.quantity + 1) > maxQuantityPerVariant) {
      return;
    }
    if (!this.isInBundle){
      this.mutations.addItemToBundle({
        productId: this.productId,
        quantity: 1,
      });
    } else {
      this.mutations.setItemQuantity(this.productId, this.quantity + 1);
    }
  }

  /**
   * @param {Event} event
   */
  handleQuantityChange(event) {
    if (this.isSoldOut) return;
    const target = event.target;
    const maxQuantityPerVariant = this.state.bundleSettings?.max_quantity_per_variant;
    if (target instanceof HTMLInputElement) {
      const isNaN = Number.isNaN(parseInt(target.value));
      let computedQuantity = isNaN ? 0 : parseInt(target.value);
      if (this.isQuantityIncreaseLocked && computedQuantity > this.quantity) {
        computedQuantity = this.quantity;
      }
      if (maxQuantityPerVariant && computedQuantity > maxQuantityPerVariant) {
        computedQuantity = maxQuantityPerVariant;
      }
      const changeInQuantity = computedQuantity - this.quantity;
      if (changeInQuantity > 0 && changeInQuantity > this.getters.getItemsToMaximum()) {
        computedQuantity = this.quantity + this.getters.getItemsToMaximum();
      }
      if (!this.isInBundle && computedQuantity > 0) {
        this.mutations.addItemToBundle({
          productId: this.productId,
          quantity: Math.max(this.minQuantity, computedQuantity),
        });
      } else {
        this.mutations.setItemQuantity(this.productId, Math.max(this.minQuantity, computedQuantity));
      }
    }
  }

  onStateUpdate() {
    this.product = this.getters.getProduct(this.productId);
    this.#renderQuantityInput();
    this.#renderAriaLabels();
    this.#renderMaxWarning();
    this.#renderSoldOutForNewMessage();
  }

  #renderAriaLabels() {
    const productTitle = this.product?.title;
    if (!productTitle) return;

    const minusBtn = /** @type {HTMLButtonElement} */ (this.refs.quantityMinusButton);
    const plusBtn = /** @type {HTMLButtonElement} */ (this.refs.quantityPlusButton);
    const decreaseBase = minusBtn.dataset.ariaLabelBase;
    const increaseBase = plusBtn.dataset.ariaLabelBase;

    if (decreaseBase) {
      minusBtn.setAttribute('aria-label', `${decreaseBase}: ${productTitle}`);
    }
    if (increaseBase) {
      plusBtn.setAttribute('aria-label', `${increaseBase}: ${productTitle}`);
    }
  }
  
  #renderQuantityInput() {
    const plusButtonIsDisabled = this.isSoldOut || this.isQuantityIncreaseLocked || this.getters.getItemsToMaximum() === 0 || this.quantity >= this.maxQuantity;
    const minusButtonIsDisabled = this.quantity <= this.minQuantity;
    const input = /** @type {HTMLInputElement} */ (this.refs.quantityInput);
    input.value = this.quantity.toString();
    input.max = (this.isQuantityIncreaseLocked ? this.quantity : this.maxQuantity).toString();
    input.disabled = this.isSoldOut;
    if (this.isSoldOut) {
      input.setAttribute('aria-disabled', 'true');
    } else {
      input.removeAttribute('aria-disabled');
    }
    /** @type {HTMLButtonElement} */ (this.refs.quantityPlusButton).disabled = plusButtonIsDisabled;
    /** @type {HTMLButtonElement} */ (this.refs.quantityMinusButton).disabled = minusButtonIsDisabled;
    if (plusButtonIsDisabled) {
      /** @type {HTMLButtonElement} */ (this.refs.quantityPlusButton).setAttribute('aria-disabled', 'true');
    } else {
      /** @type {HTMLButtonElement} */ (this.refs.quantityPlusButton).removeAttribute('aria-disabled');
    }
    if (minusButtonIsDisabled) {
      /** @type {HTMLButtonElement} */ (this.refs.quantityMinusButton).setAttribute('aria-disabled', 'true');
    } else {
      /** @type {HTMLButtonElement} */ (this.refs.quantityMinusButton).removeAttribute('aria-disabled');
    }
  }

  #syncInputMobileBehavior = () => {
    const input = /** @type {HTMLInputElement} */ (this.refs.quantityInput);
    const isMobile = this.#quantityInputMobileQuery.matches;
    input.readOnly = isMobile;
    if (isMobile) {
      input.setAttribute('inputmode', 'none');
    } else {
      input.removeAttribute('inputmode');
    }
  };

  #blurQuantityInput() {
    const input = /** @type {HTMLInputElement} */ (this.refs.quantityInput);
    if (document.activeElement === input) {
      input.blur();
    }
  }

  #renderMaxWarning() {
    const tooltip = /** @type {HTMLElement} */ (this.refs.quantitySelectorMaxWarningTooltip);
    const plusButton = /** @type {HTMLButtonElement} */ (this.refs.quantityPlusButton);
    let maxWarningLabel = null;
    if (this.isQuantityIncreaseLocked) {
      maxWarningLabel = this.dataset.warningSoldOutForNew ?? '';
    } else if (this.getters.getItemsToMaximum() === 0) {
      const { max } = this.getters.getRechargeSettingsRange();
      maxWarningLabel = (this.dataset.warningGlobalMaximum ?? '').replace('[count]', max.toString());
    }
    const maxQuantityPerVariant = this.state.bundleSettings?.max_quantity_per_variant;
    if (maxQuantityPerVariant && this.quantity >= maxQuantityPerVariant) {
      maxWarningLabel = (this.dataset.warningVariantMaximum ?? '').replace('[count]', maxQuantityPerVariant.toString());
    }
    tooltip.textContent = maxWarningLabel ?? '';
    tooltip.hidden = !maxWarningLabel;
    if (maxWarningLabel && tooltip.id) {
      plusButton.setAttribute('aria-describedby', tooltip.id);
    } else {
      plusButton.removeAttribute('aria-describedby');
    }
  }

  #renderSoldOutForNewMessage() {
    const message = /** @type {HTMLElement} */ (this.refs.soldOutForNewMessage);
    const input = /** @type {HTMLInputElement} */ (this.refs.quantityInput);
    const showMessage = this.isQuantityIncreaseLocked;
    message.hidden = !showMessage;
    if (showMessage && message.id) {
      input.setAttribute('aria-describedby', message.id);
    } else if (!this.isQuantityIncreaseLocked) {
      input.removeAttribute('aria-describedby');
    }
  }
}

if (!customElements.get('bundle-builder-quantity-selector-component')) {
  customElements.define('bundle-builder-quantity-selector-component', BundleBuilderQuantitySelectorComponent);
}